import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Checkbox, FormControlLabel, Slider, TextField, Typography } from '@mui/material';
import VideoApp from './VideoApp';
import { synchronizePlayers } from '../libs/videoSynchronization';

export default function SynchronizedVideos({ cameraSrc, screenSrc, hasCamera, synchronization, seekRequest, cameraEvents, screenEvents }) {
    const players = useRef({});
    const controller = useRef(null);
    const automaticOffset = Number.isFinite(synchronization?.cameraOffsetSeconds) ? synchronization.cameraOffsetSeconds : null;
    const [offset, setOffset] = useState(automaticOffset || 0);
    const [offsetInput, setOffsetInput] = useState(String(automaticOffset || 0));
    const [enabled, setEnabled] = useState(true);
    const [height, setHeight] = useState(28);
    const [stacked, setStacked] = useState(false);
    const [readyCount, setReadyCount] = useState(0);
    const [error, setError] = useState('');
    const readyCamera = useCallback(player => {
        players.current.camera = player;
        if (player) setReadyCount(count => count + 1);
    }, []);
    const readyScreen = useCallback(player => {
        players.current.screen = player;
        if (player) setReadyCount(count => count + 1);
    }, []);

    useEffect(() => {
        if (!enabled || !hasCamera || !players.current.screen || !players.current.camera) return;
        const sync = synchronizePlayers(players.current.screen, players.current.camera, offset, setError);
        controller.current = sync;
        return () => { sync.dispose(); controller.current = null; };
    }, [enabled, hasCamera, offset, readyCount]);

    useEffect(() => {
        if (!seekRequest) return;
        const source = players.current[seekRequest.source];
        if (!source) return;
        const apply = () => {
            if (controller.current) controller.current.seek(source, seekRequest.time);
            else source.currentTime(seekRequest.time);
        };
        if (source.readyState() > 0) apply();
        else source.one('loadedmetadata', apply);
        return () => source.off('loadedmetadata', apply);
    }, [seekRequest, readyCount]);

    return <Box className="watch-videos">
        <Box className="watch-video-controls">
            <Typography>Размер видео</Typography>
            <Slider aria-label="Размер видео" value={height} min={20} max={65} onChange={(event, value) => setHeight(value)} sx={{ width: 160 }} />
            <Button onClick={() => setStacked(value => !value)}>{stacked ? 'Расположить рядом' : 'Друг под другом'}</Button>
            {hasCamera && <>
                <FormControlLabel control={<Checkbox checked={enabled} onChange={event => setEnabled(event.target.checked)} />} label="Синхронное воспроизведение" />
                <TextField label="Смещение камеры, с" type="number" size="small" value={offsetInput} inputProps={{ step: 0.1, min: -86400, max: 86400 }}
                    onChange={event => {
                        setOffsetInput(event.target.value);
                        const value = Number(event.target.value);
                        if (event.target.value !== '' && Number.isFinite(value) && Math.abs(value) <= 86400) setOffset(value);
                    }} onBlur={() => setOffsetInput(String(offset))} sx={{ width: 180 }} />
                <Button onClick={() => { setOffset(automaticOffset || 0); setOffsetInput(String(automaticOffset || 0)); }}>Сбросить смещение</Button>
            </>}
        </Box>
        {hasCamera && <Typography variant="body2" sx={{ mb: 1 }}>
            {automaticOffset === null ? 'Для этой записи время старта потоков неизвестно. Подберите смещение по общему событию.' : 'Смещение рассчитано по времени согласованного запуска записи.'}
            {' Положительное смещение: камера показывает больше секунд, чем экран. Перемотка, пауза и скорость действуют на оба видео.'}
        </Typography>}
        {error && <Typography color="error">{error}</Typography>}
        <Box className={`watch-player-grid${stacked || !hasCamera ? ' watch-player-stacked' : ''}`}
            style={{ '--watch-video-height': `${height}vh`, '--watch-video-max': `${height * 16}px` }}>
            <Box className="watch-player-card" data-stream="screen">
                <Typography variant="h6">Экран</Typography>
                <Box className="watch-video-frame"><VideoApp src={screenSrc} autoPlay={false} onReady={readyScreen} muted={hasCamera} /></Box>
                {screenEvents}
            </Box>
            {hasCamera && <Box className="watch-player-card" data-stream="camera">
                <Typography variant="h6">Камера</Typography>
                <Box className="watch-video-frame"><VideoApp src={cameraSrc} autoPlay={false} onReady={readyCamera} muted={false} /></Box>
                {cameraEvents}
            </Box>}
        </Box>
    </Box>;
}
